import { useEffect, useRef, useState } from 'react';

interface SectionTab {
  id: string;
  label: string;
}

interface MobileSectionTabsProps {
  tabs: SectionTab[];
  /** Top offset (px) to sit under the site header. Defaults to 57 (Layout header height on mobile). */
  topOffset?: number;
}

/**
 * Sticky horizontal scrollable tab strip with scroll-spy.
 * - Highlights the active section as the user scrolls
 * - Tapping a tab smooth-scrolls to its anchored section
 * - The active tab auto-scrolls into view inside the strip
 */
export function MobileSectionTabs({ tabs, topOffset = 57 }: MobileSectionTabsProps) {
  const [active, setActive] = useState(tabs[0]?.id);
  const stripRef = useRef<HTMLDivElement>(null);
  const clickLockRef = useRef(false);

  // Scroll-spy: observe each section
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    tabs.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (clickLockRef.current) return;
          if (entry.isIntersecting) setActive(id);
        },
        { rootMargin: `-${topOffset + 60}px 0px -55% 0px`, threshold: 0 },
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, [tabs, topOffset]);

  // Auto-scroll active tab into view inside the strip
  useEffect(() => {
    if (!stripRef.current) return;
    const btn = stripRef.current.querySelector(`[data-tab-id="${active}"]`) as HTMLElement | null;
    if (!btn) return;
    const stripRect = stripRef.current.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    const offset = btnRect.left - stripRect.left - stripRect.width / 2 + btnRect.width / 2;
    stripRef.current.scrollBy({ left: offset, behavior: 'smooth' });
  }, [active]);

  const handleClick = (id: string) => {
    setActive(id);
    // Lock scroll-spy briefly so it doesn't fight the smooth scroll
    clickLockRef.current = true;
    setTimeout(() => {
      clickLockRef.current = false;
    }, 800);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div
      className="lg:hidden sticky z-30 bg-background border-b"
      style={{ top: `${topOffset}px` }}
    >
      <div
        ref={stripRef}
        className="overflow-x-auto no-scrollbar relative after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-8 after:bg-gradient-to-l after:from-background after:to-transparent"
        style={{ scrollbarWidth: 'none' }}
      >
        <div className="inline-flex w-max">
          {tabs.map(tab => (
            <button
              key={tab.id}
              data-tab-id={tab.id}
              onClick={() => handleClick(tab.id)}
              className={`shrink-0 px-4 py-3 text-sm font-semibold whitespace-nowrap relative transition-colors ${
                active === tab.id ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              {tab.label}
              {active === tab.id && (
                <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-primary rounded-full" />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
